import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Star, Loader2, CheckCircle2, AlertTriangle } from "lucide-react";
import api from "../lib/api";
import useSeo from "../hooks/useSeo";

export default function LeaveReview() {
  useSeo("Leave a review — Mr. Resurface", "", { noindex: true });
  const { token } = useParams();
  const [state, setState] = useState("loading");
  const [info, setInfo] = useState(null);
  const [author, setAuthor] = useState("");
  const [rating, setRating] = useState(5);
  const [content, setContent] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api
      .get(`/review/${token}`)
      .then(({ data }) => {
        setInfo(data);
        setAuthor(data.name && data.name !== "there" ? data.name : "");
        setState(data.already_submitted ? "done" : "form");
      })
      .catch(() => setState("invalid"));
  }, [token]);

  const submit = async () => {
    setError("");
    if (content.trim().length < 10) {
      setError("Please write a sentence or two so we can share your review.");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/review/${token}`, { author: author.trim(), rating, content });
      setState("done");
    } catch (e) {
      setError(e?.response?.data?.detail || "Something went wrong — please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="bg-[#f7f7f7] py-14" data-testid="leave-review-page">
      <div className="mx-auto max-w-xl px-5">
        <div className="border-t-[5px] border-[#244aa2] bg-white p-7 shadow-sm sm:p-9">
          {state === "loading" && (
            <div className="flex justify-center py-10">
              <Loader2 className="h-7 w-7 animate-spin text-[#244aa2]" />
            </div>
          )}

          {state === "invalid" && (
            <div data-testid="leave-review-invalid">
              <AlertTriangle className="h-8 w-8 text-[#a10000]" />
              <h1 className="mr-h1 mt-4 text-2xl">This review link is no longer valid</h1>
              <p className="mt-3 text-[15px] leading-relaxed text-[#575757]">
                Please use the most recent link we e-mailed you, or call us on{" "}
                <a href="tel:+12035785260" className="font-bold text-[#244aa2]">(203) 578-5260</a>{" "}
                and we will help.
              </p>
            </div>
          )}

          {state === "done" && (
            <div data-testid="leave-review-thanks">
              <CheckCircle2 className="h-9 w-9 text-[#1a7f37]" />
              <h1 className="mr-h1 mt-4 text-2xl">Thank you</h1>
              <p className="mt-3 text-[15px] leading-relaxed text-[#575757]">
                Your review has been sent straight to Ernest. We read every single one, and we
                appreciate you taking the time.
              </p>
              <p className="mt-2 text-[13px] text-neutral-500">
                Mr. Resurface LLC · Wolcott, Connecticut · (203) 578-5260
              </p>
            </div>
          )}

          {state === "form" && (
            <div>
              <h1 className="mr-h1 text-2xl sm:text-3xl">Leave a review</h1>
              <p className="mt-3 text-[15px] leading-relaxed text-[#575757]">
                Thank you for taking a moment. Honest feedback is what we want — positive or not.
              </p>

              <label className="mt-7 block text-[12px] font-bold uppercase tracking-widest text-neutral-500">
                Your name
              </label>
              <input
                data-testid="review-author-input"
                value={author}
                onChange={(e) => setAuthor(e.target.value)}
                placeholder="First name and town"
                className="mt-2 h-12 w-full border border-neutral-300 px-3 text-[15px] focus:border-[#244aa2] focus:outline-none"
              />

              <p className="mt-6 text-[12px] font-bold uppercase tracking-widest text-neutral-500">
                Your rating
              </p>
              <div className="mt-2 flex gap-1.5" role="radiogroup" aria-label="Star rating">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button
                    key={n}
                    type="button"
                    aria-label={`${n} star${n > 1 ? "s" : ""}`}
                    aria-checked={rating === n}
                    role="radio"
                    data-testid={`review-star-${n}`}
                    onClick={() => setRating(n)}
                    className="transition-transform duration-150 hover:scale-110"
                  >
                    <Star
                      className={`h-9 w-9 ${
                        n <= rating ? "fill-[#f0a92b] text-[#f0a92b]" : "text-neutral-300"
                      }`}
                    />
                  </button>
                ))}
              </div>

              <label className="mt-6 block text-[12px] font-bold uppercase tracking-widest text-neutral-500">
                Your review
              </label>
              <textarea
                data-testid="review-content-input"
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="Tell us about the work we did for you."
                className="mt-2 h-36 w-full border border-neutral-300 p-3 text-[15px] leading-relaxed focus:border-[#244aa2] focus:outline-none"
              />

              {error && (
                <p role="alert" data-testid="review-error" className="mt-3 text-[14px] font-semibold text-[#a10000]">
                  {error}
                </p>
              )}

              <button
                type="button"
                data-testid="review-submit-button"
                onClick={submit}
                disabled={saving}
                className="mt-6 w-full bg-[#244aa2] px-6 py-4 text-[13px] font-bold uppercase tracking-[0.08em] text-white transition-colors duration-200 hover:bg-[#0c2a66] disabled:opacity-60"
              >
                {saving ? "Sending…" : "Submit my review"}
              </button>

              <p className="mt-4 text-[12.5px] leading-relaxed text-neutral-500">
                Reviews are read by the owner before they appear on mrresurface.com.
              </p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
